import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import service from '../service';
import type {
  PreviewRegistrationCodePayload,
  RegistrationCodePreview,
} from '../types/registrationCode';
import { inviteCodeQueryKeys } from './useInviteCodes';

const PREVIEW_DEBOUNCE_MS = 400;

type DebouncedPayload = {
  serialized: string | null;
  payload: PreviewRegistrationCodePayload | null;
};

type UseInviteCodePreviewParams = {
  /** `null` while the form is invalid; the last preview stays on screen. */
  payload: PreviewRegistrationCodePayload | null;
  enabled?: boolean;
};

export function useInviteCodePreview({
  payload,
  enabled = true,
}: UseInviteCodePreviewParams) {
  const serialized = payload ? JSON.stringify(payload) : null;
  const [debounced, setDebounced] = useState<DebouncedPayload>({
    serialized,
    payload,
  });

  useEffect(() => {
    const timer = setTimeout(
      () => setDebounced({ serialized, payload }),
      PREVIEW_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
    // `serialized` captures every change to `payload`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  const debouncedPayload = debounced.payload;

  const query = useQuery<RegistrationCodePreview, Error>({
    queryKey: inviteCodeQueryKeys.preview(debounced.serialized),
    enabled: enabled && debouncedPayload !== null,
    queryFn: async () => {
      if (!debouncedPayload) {
        throw new Error('Invite code preview needs a valid form.');
      }
      return service.previewInviteCode(debouncedPayload);
    },
    placeholderData: (previous) => previous,
  });

  return {
    ...query,
    isUpdating: serialized !== debounced.serialized || query.isFetching,
  };
}
